// weitereWege.ts — weitere Kommunikationswege der Kontaktanlage.
//
// Die Masken haben je Art EIN festes Feld (bei der Person je Spalte), ein
// Kontakt aber beliebig viele Nummern und Adressen: das Diensthandy, die
// Vertriebs-Durchwahl, die zweite Mail. Sie liegen bis zum Speichern als
// JSON-Liste im Formularfeld `weitereKommunikation` — dasselbe Feld, in das
// der „Daten befüllen"-Abgleich der Firma schon seine Impressum-Treffer legt.
// Die Payload-Builder machen daraus eigene ContactCommunication-Zeilen.

import { normalizeEmail, normalizePhone, validateEmail, validatePhone } from "../validation";

export type WegTyp = "phone" | "mobile" | "fax" | "email";
export type WegKind = "privat" | "geschaeftlich";

export type WeitererWeg = {
  id: string;
  type: WegTyp;
  value: string;
  /** Nur bei Personen: in welcher Spalte der Weg steht. */
  kind?: WegKind;
  /** Bezeichnung, z. B. „Diensthandy" oder „Vertrieb". */
  label?: string;
  /** Vom Impressum-Abgleich der Firma übernommen. */
  quelle?: "impressum";
};

export const WEG_TYPEN: readonly WegTyp[] = ["phone", "mobile", "fax", "email"];

export const WEG_TYP_LABEL: Record<WegTyp, string> = {
  phone: "Telefon",
  mobile: "Mobil",
  fax: "Fax",
  email: "E-Mail",
};

let zaehler = 0;
export const neueWegId = (): string => `weg-${Date.now().toString(36)}-${++zaehler}`;

const istTyp = (v: unknown): v is WegTyp => typeof v === "string" && (WEG_TYPEN as readonly string[]).includes(v);
const text = (v: unknown): string => (typeof v === "string" ? v : "");

/** Liest das Formularfeld. Unlesbares ergibt eine leere Liste, fehlende ids werden vergeben. */
export function wegeLesen(raw: unknown): WeitererWeg[] {
  let roh: unknown;
  try {
    roh = JSON.parse(typeof raw === "string" && raw.trim() ? raw : "[]");
  } catch {
    return [];
  }
  if (!Array.isArray(roh)) return [];
  const wege: WeitererWeg[] = [];
  roh.forEach((e, index) => {
    const eintrag = (e ?? {}) as Record<string, unknown>;
    if (!istTyp(eintrag.type)) return;
    const weg: WeitererWeg = {
      // Ohne id (Altform des Impressum-Abgleichs) eine STABILE aus der
      // Position — sie dient als React-Key, eine neue je Render nähme dem
      // Feld beim Tippen den Fokus.
      id: text(eintrag.id) || `weg-alt-${index}`,
      type: eintrag.type,
      // Ein gerade geleerter Wert bleibt stehen — sonst verschwände das Feld
      // unter dem Cursor. Leere Zeilen verwirft erst der Payload-Builder.
      value: text(eintrag.value),
    };
    if (eintrag.kind === "privat" || eintrag.kind === "geschaeftlich") weg.kind = eintrag.kind;
    if (text(eintrag.label).trim()) weg.label = text(eintrag.label).trim();
    if (eintrag.quelle === "impressum") weg.quelle = "impressum";
    wege.push(weg);
  });
  return wege;
}

export const wegeSchreiben = (wege: WeitererWeg[]): string => JSON.stringify(wege);

/** Wege einer Art — und, wenn angegeben, einer Spalte. */
export const wegeFuer = (wege: WeitererWeg[], type: WegTyp, kind?: WegKind): WeitererWeg[] =>
  wege.filter((w) => w.type === type && (kind === undefined || w.kind === kind));

/** „Mobil (Diensthandy)" oder fortlaufend „Mobil 2" — das feste Feld ist Nummer 1. */
export const wegLabel = (weg: WeitererWeg, nr: number): string =>
  weg.label ? `${WEG_TYP_LABEL[weg.type]} (${weg.label})` : `${WEG_TYP_LABEL[weg.type]} ${nr}`;

/** Fehlertext oder null. Anders als die festen Felder ist ein leerer Wert hier ein Fehler. */
export function wegPruefen(type: WegTyp, value: string): string | null {
  if (!value.trim()) return type === "email" ? "Bitte eine E-Mail-Adresse angeben." : "Bitte eine Nummer angeben.";
  const r = type === "email" ? validateEmail(value) : validatePhone(value);
  return r.valid ? null : r.error ?? "Ungültige Eingabe.";
}

/** Speicherform wie bei den festen Feldern (E.164, Domain klein). */
export const wegNormalisieren = (type: WegTyp, value: string): string =>
  type === "email" ? normalizeEmail(value) : normalizePhone(value);

/** Für die Formatprüfung beim Speichern: leere Zusatzfelder sperren nicht, sie fallen weg. */
export const wegFormatOk = (weg: WeitererWeg): boolean => !weg.value.trim() || wegPruefen(weg.type, weg.value) === null;
